import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  Compass,
  Users,
  Wallet,
  Snowflake,
  IdCard,
  Ruler,
  Award,
  RefreshCcw,
  Share2,
  Phone,
  Mail,
  Home,
  Sparkles,
  TrendingDown,
} from "lucide-react";
import { getFyndanuListings, type FyndanuListing } from "@/lib/fyndanu.functions";
import { PageHeader } from "./fordon";
import { PriceWatchForm } from "@/components/PriceWatchForm";
import { vehicleSlug } from "@/lib/vehicle-slug";

// Budgetband → övre gräns i kronor för prisvakt.
function budgetCeiling(b: OptionValue | undefined): number | null {
  if (b === "u400") return 400_000;
  if (b === "400-700") return 700_000;
  if (b === "700-1100") return 1_100_000;
  return null; // o1100 = ingen övre gräns
}

// Sovplatser → minimum för prisvakt.
function sleepsMin(f: OptionValue | undefined): number | null {
  if (f === "1-2") return 2;
  if (f === "3-4") return 4;
  if (f === "5+") return 5;
  return null;
}

const URL_SELF = "https://www.akhusbilfritid.se/husbil-matchning";

// ---------------------------------------------------------------
// Quiz definition — 6 short questions, deterministic scoring
// against real live inventory. Result URL is shareable (?a=...).
// This is a genuinely unique tool: no other Swedish RV dealer
// scores their live stock against a personal profile in real time.
// ---------------------------------------------------------------

type OptionValue = string;
type QuestionKey = "vehicleType" | "family" | "budget" | "season" | "license" | "length" | "experience";

interface QuizOption {
  value: OptionValue;
  label: string;
  hint?: string;
}
interface Question {
  key: QuestionKey;
  title: string;
  sub: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  options: QuizOption[];
}

const QUESTIONS: Question[] = [
  {
    key: "vehicleType",
    title: "Husbil eller husvagn?",
    sub: "Vi matchar mot båda kategorierna i vårt live-lager.",
    icon: Home,
    options: [
      { value: "husbil", label: "Husbil", hint: "Kör direkt, boende + bil i ett" },
      { value: "husvagn", label: "Husvagn", hint: "Dras av bilen, ofta billigare i drift" },
      { value: "bada", label: "Visa båda", hint: "Öppen för det som passar bäst" },
    ],
  },
  {
    key: "family",
    title: "Hur många ska sova ombord?",
    sub: "Sovplatser styr planlösning mer än något annat.",
    icon: Users,
    options: [
      { value: "1-2", label: "1–2 personer", hint: "Par eller solo" },
      { value: "3-4", label: "3–4 personer", hint: "Familj med barn" },
      { value: "5+", label: "5 eller fler", hint: "Storfamilj / kompisgäng" },
    ],
  },
  {
    key: "budget",
    title: "Vilken prisnivå letar du i?",
    sub: "Vi visar bara det som faktiskt finns i lager.",
    icon: Wallet,
    options: [
      { value: "u400", label: "Under 400 000 kr" },
      { value: "400-700", label: "400 000 – 700 000 kr" },
      { value: "700-1100", label: "700 000 – 1 100 000 kr" },
      { value: "o1100", label: "Över 1 100 000 kr" },
    ],
  },
  {
    key: "season",
    title: "När ska du använda den?",
    sub: "Vintercertifiering och värmesystem avgör mycket.",
    icon: Snowflake,
    options: [
      { value: "sommar", label: "Främst sommar" },
      { value: "3sason", label: "3-säsong (vår–höst)" },
      { value: "vinter", label: "Året runt / vinter i Norrland" },
    ],
  },
  {
    key: "license",
    title: "Vilket körkort har du?",
    sub: "B tar dig upp till 3 500 kg totalvikt.",
    icon: IdCard,
    options: [
      { value: "B", label: "Bara B", hint: "≤ 3 500 kg" },
      { value: "B96", label: "B96 / BE", hint: "≤ 4 250 kg" },
      { value: "C1", label: "C1 eller högre", hint: "Fri viktklass" },
    ],
  },
  {
    key: "length",
    title: "Hur lång får den vara?",
    sub: "Tänk på garage, färja och parkering hemma.",
    icon: Ruler,
    options: [
      { value: "kort", label: "Under 6,5 m", hint: "Van / plåtis" },
      { value: "mellan", label: "6,5 – 7,4 m", hint: "Halvintegrerad" },
      { value: "lang", label: "7,5 m eller mer", hint: "Alkov / integrerad" },
    ],
  },
  {
    key: "experience",
    title: "Hur van är du?",
    sub: "Vi låter det påverka rekommendationen — inte urvalet.",
    icon: Award,
    options: [
      { value: "ny", label: "Förstagångsköpare" },
      { value: "van", label: "Har haft husbil tidigare" },
      { value: "proffs", label: "Rutinerad — vet vad jag vill ha" },
    ],
  },
];

type Answers = Partial<Record<QuestionKey, OptionValue>>;

// ---------- Scoring ----------
// STRICT MODE: hårda filter först — om ett fordon bryter mot en absolut
// begränsning (körkort, sovplatser, budget, längd) exkluderas det helt.
// Vi vill hellre visa 0 träffar än fel bil. Saknas kritisk data → exkludera
// hellre än gissa (utom säsong där neutralt är ok).

const NEUTRAL = 60;

function passesHardFilters(a: Answers, l: FyndanuListing): boolean {
  // Fordonstyp — respektera användarens val (husbil / husvagn / båda).
  if (a.vehicleType === "husbil" && l.category !== "husbil") return false;
  if (a.vehicleType === "husvagn" && l.category !== "husvagn") return false;
  // Körkort — får ALDRIG matcha ett tyngre fordon än körkortet klarar.
  if (a.license === "B") {
    if (l.totalWeight == null) return false;
    if (l.totalWeight > 3500) return false;
  }
  if (a.license === "B96") {
    if (l.totalWeight == null) return false;
    if (l.totalWeight > 4250) return false;
  }
  // Sovplatser — matcha aldrig en bil med för få bäddar.
  if (a.family) {
    if (l.sleepingSpots == null) return false;
    if (a.family === "1-2" && l.sleepingSpots < 2) return false;
    if (a.family === "3-4" && l.sleepingSpots < 3) return false;
    if (a.family === "5+" && l.sleepingSpots < 5) return false;
  }
  // Budget — hårt tak +15 %, hårt golv 60 % av bandets nedre gräns.
  if (a.budget) {
    if (l.price == null) return false;
    const caps: Record<string, number> = {
      u400: 400_000 * 1.15,
      "400-700": 700_000 * 1.15,
      "700-1100": 1_100_000 * 1.15,
      o1100: Infinity,
    };
    const floors: Record<string, number> = {
      u400: 0,
      "400-700": 400_000 * 0.6,
      "700-1100": 700_000 * 0.6,
      o1100: 1_100_000 * 0.6,
    };
    if (l.price > caps[a.budget]) return false;
    if (l.price < floors[a.budget]) return false;
  }
  // Längd — hårda gränser.
  if (a.length) {
    if (l.length == null) return false;
    if (a.length === "kort" && l.length > 7.0) return false;
    if (a.length === "mellan" && (l.length < 6.0 || l.length > 7.9)) return false;
    if (a.length === "lang" && l.length < 7.0) return false;
  }
  return true;
}

function scoreSleepingSpots(a: OptionValue | undefined, spots: number | null): number {
  if (!a) return NEUTRAL;
  if (spots == null) return NEUTRAL;
  if (a === "1-2") return spots <= 2 ? 100 : spots === 3 ? 78 : 55;
  if (a === "3-4") return spots >= 3 && spots <= 4 ? 100 : spots === 5 ? 82 : spots === 2 ? 60 : 40;
  if (a === "5+") return spots >= 5 ? 100 : spots === 4 ? 70 : 35;
  return NEUTRAL;
}

function scoreBudget(a: OptionValue | undefined, price: number | null): number {
  if (!a || price == null) return NEUTRAL;
  const bands: Record<string, [number, number]> = {
    u400: [0, 400_000],
    "400-700": [400_000, 700_000],
    "700-1100": [700_000, 1_100_000],
    o1100: [1_100_000, Infinity],
  };
  const [lo, hi] = bands[a];
  if (price >= lo && price <= hi) return 100;
  if (price > hi && price <= hi * 1.15) return 78;
  if (price < lo && price >= lo * 0.85) return 82;
  return 30;
}

function scoreSeason(a: OptionValue | undefined, l: FyndanuListing): number {
  if (!a) return NEUTRAL;
  const eq = (l.equipment ?? []).join(" ").toLowerCase();
  const winter = /alde|golv[- ]?värme|dieselvärme|isoler|vinter|truma combi|alkove?n?.*isoler/i.test(
    l.description + " " + eq,
  );
  if (a === "vinter") return winter ? 100 : 55;
  if (a === "3sason") return winter ? 92 : 78;
  return 85;
}

function scoreLicense(a: OptionValue | undefined, weight: number | null): number {
  if (!a || weight == null) return NEUTRAL;
  if (a === "B") return weight <= 3500 ? 100 : 15;
  if (a === "B96") return weight <= 4250 ? 100 : 40;
  return 100;
}

function scoreLength(a: OptionValue | undefined, length: number | null): number {
  if (!a || length == null) return NEUTRAL;
  if (a === "kort") return length < 6.5 ? 100 : length < 7 ? 55 : 20;
  if (a === "mellan") return length >= 6.5 && length <= 7.4 ? 100 : length < 6.5 ? 75 : 55;
  if (a === "lang") return length >= 7.5 ? 100 : length >= 7 ? 70 : 40;
  return NEUTRAL;
}

function scoreListing(a: Answers, l: FyndanuListing): number {
  const parts: number[] = [
    scoreSleepingSpots(a.family, l.sleepingSpots),
    scoreBudget(a.budget, l.price),
    scoreSeason(a.season, l),
    scoreLicense(a.license, l.totalWeight),
    scoreLength(a.length, l.length),
  ];
  const avg = parts.reduce((s, n) => s + n, 0) / parts.length;
  return Math.min(100, avg + (l.isFeatured ? 3 : 0));
}

function explainMatch(a: Answers, l: FyndanuListing): string[] {
  const out: string[] = [];
  if (a.family && l.sleepingSpots != null && scoreSleepingSpots(a.family, l.sleepingSpots) >= 90)
    out.push(`${l.sleepingSpots} sovplatser passar familjestorleken`);
  if (a.budget && l.price != null && scoreBudget(a.budget, l.price) >= 90)
    out.push(`Pris ${l.price.toLocaleString("sv-SE")} kr ligger i din budget`);
  if (a.license && l.totalWeight != null && scoreLicense(a.license, l.totalWeight) >= 90)
    out.push(`Totalvikt ${l.totalWeight.toLocaleString("sv-SE")} kg klaras med ${a.license}-körkort`);
  if (a.length && l.length != null && scoreLength(a.length, l.length) >= 90)
    out.push(`Längd ${l.length} m matchar önskemålet`);
  if (a.season === "vinter" && scoreSeason(a.season, l) >= 90)
    out.push("Vintercertifierad / isolerad för Norrland");
  if (l.isFeatured) out.push("Utvald bil av oss");
  return out.slice(0, 4);
}

// Undre budget-gräns för användarens valda band — används för "under budget"-badge.
function budgetFloor(a: OptionValue | undefined): number | null {
  if (!a) return null;
  if (a === "u400") return 0;
  if (a === "400-700") return 400_000;
  if (a === "700-1100") return 700_000;
  if (a === "o1100") return 1_100_000;
  return null;
}

// Rolig, delbar persona baserat på svaren.
function derivePersona(a: Answers): { title: string; blurb: string } | null {
  const answered = Object.values(a).filter(Boolean).length;
  if (answered < 3) return null;
  if (a.season === "vinter") {
    return {
      title: "Vinter-nomaden ❄️",
      blurb: "Trivs där andra vänder — du vill ha isolering, diesel- eller golvvärme och laddad batteribank.",
    };
  }
  if (a.family === "5+") {
    return {
      title: "Storfamiljs-kaptenen 👨‍👩‍👧‍👦",
      blurb: "Sovplatser före allt annat — bäddar, bälten och smart planlösning avgör.",
    };
  }
  if (a.vehicleType === "husvagn") {
    return {
      title: "Baslägrs-strategen 🏕️",
      blurb: "Bilen kvar, husvagnen på plats. Låg driftkostnad och stort boende per krona.",
    };
  }
  if (a.length === "kort" && (a.family === "1-2" || !a.family)) {
    return {
      title: "Weekend-warrior 🚐",
      blurb: "Kompakt van som tar sig fram överallt — spontan, smidig, alltid redo.",
    };
  }
  if (a.budget === "o1100") {
    return {
      title: "Premium-resenären 🥂",
      blurb: "Kvalitet före kompromiss — integrerad chassikomfort och fullutrustning.",
    };
  }
  if (a.experience === "ny") {
    return {
      title: "Förstagångs-utforskaren 🧭",
      blurb: "Sikta på beprövade planlösningar och lätta att köra — vi vägleder resten.",
    };
  }
  return {
    title: "Frihets-sökaren 🌲",
    blurb: "Öppet sinne, klar riktning. Vi lyfter fram bilar som passar din stil.",
  };
}

// ---------- URL state (shareable results) ----------

function encode(a: Answers): string {
  return QUESTIONS.map((q) => a[q.key] ?? "-").join(".");
}
function decode(s: string | null): Answers {
  if (!s) return {};
  const parts = s.split(".");
  const out: Answers = {};
  QUESTIONS.forEach((q, i) => {
    const v = parts[i];
    if (v && v !== "-" && q.options.some((o) => o.value === v)) out[q.key] = v;
  });
  return out;
}

// ---------- FAQ (for schema + on-page) ----------

const FAQ = [
  {
    q: "Hur fungerar husbils-matchningen?",
    a: "Du svarar på sju korta frågor om fordonstyp (husbil eller husvagn), sovplatser, budget, säsong, körkort, längd och erfarenhet. Vi jämför svaren mot varje husbil och husvagn i vårt live-lager och räknar fram en träffprocent per fordon. Ingen mail, inget konto — resultatet visas direkt.",
  },
  {
    q: "Är resultatet objektivt?",
    a: "Ja. Poängen räknas ut med samma formel för varje fordon oavsett pris eller marginal. Om ett fordon saknar en teknisk spec ger vi det ett neutralt värde istället för att straffa det — så du inte missar en bra bil bara för att annonstexten är kort.",
  },
  {
    q: "Vad händer om inga fordon matchar just nu?",
    a: "Då visar vi de närmaste träffarna och du kan justera ett svar för att bredda urvalet. Nya fordon läggs in flera gånger i veckan från vårt lager i Kalix — vi speglar också våra annonser på Fyndanu.se i realtid.",
  },
  {
    q: "Kan jag dela resultatet?",
    a: "Ja. Adressraden uppdateras när du svarar, så du kan skicka länken till en fru, kompis eller mekaniker och de landar på exakt samma träffar.",
  },
  {
    q: "Ersätter det en provsittning?",
    a: "Nej — men det halverar tiden det tar att hitta rätt kandidater. Ring 070-862 97 43 eller kom förbi Stabsvägen 5 i Kalix när du valt ut 2–3 favoriter, så bokar vi provkörning.",
  },
];

// ---------- Route ----------

const listingsQuery = queryOptions({
  queryKey: ["fyndanu", "listings"],
  queryFn: () => getFyndanuListings(),
  staleTime: 5 * 60 * 1000,
});

export const Route = createFileRoute("/husbil-matchning")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listingsQuery),
  head: ({ loaderData }) => {
    const items = (loaderData?.items ?? []).filter(
      (l) => l.category === "husbil" || l.category === "husvagn",
    );
    const prices = items.map((i) => i.price).filter((p): p is number => !!p);
    const minP = prices.length ? Math.min(...prices) : 0;
    const maxP = prices.length ? Math.max(...prices) : 0;

    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Husbilar & husvagnar i lager – matchbara",
      numberOfItems: items.length,
      itemListElement: items.slice(0, 20).map((l, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: l.url,
        item: {
          "@type": "Product",
          name: l.title,
          image: l.image,
          brand: l.brand ? { "@type": "Brand", name: l.brand } : undefined,
          category: l.category === "husbil" ? "Husbil" : "Husvagn",
          // Product-snippets kräver offers — annars flaggar Google
          // strukturerad data som fel. Utan pris skickar vi ändå ett
          // Offer med tillgänglighet och URL.
          offers: {
            "@type": "Offer",
            ...(l.price ? { price: l.price, priceCurrency: "SEK" } : {}),
            availability: "https://schema.org/InStock",
            url: l.url,
            seller: { "@type": "AutoDealer", name: "AK Husbil & Fritid" },
          },
        },
      })),
    };

    const datasetSchema = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: "Live-lager: husbilar och husvagnar hos AK Husbil & Fritid",
      description: `Realtidsdata över ${items.length} fordon till salu i Kalix, med spec-fält som används av vår matchningsalgoritm (sovplatser, totalvikt, längd, säsong, pris).`,
      keywords: ["husbil", "husvagn", "Kalix", "Norrbotten", "begagnat", "Rapido"],
      creator: { "@type": "Organization", name: "AK Husbil & Fritid" },
      variableMeasured: ["price", "sleepingSpots", "totalWeight", "length", "year", "brand"],
      license: "https://www.akhusbilfritid.se/anvandarvillkor",
      isAccessibleForFree: true,
    };

    return {
      meta: [
        { title: "Husbil-matchning – hitta din perfekta husbil på 60 sekunder" },
        {
          name: "description",
          content: `Sju frågor. Vi matchar mot ${items.length || "vårt"} fordon i lager (${minP ? `${(minP / 1000).toFixed(0)}k` : "0"}–${maxP ? `${(maxP / 1000).toFixed(0)}k` : "0"} kr) och visar vilka som passar dig med träffprocent. Gratis, inget konto.`,
        },
        { name: "keywords", content: "hitta rätt husbil, husbil matchning, husbil quiz, vilken husbil ska jag köpa, husbil rekommendation, husbil kalkylator" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: "Husbil-matchning – 6 frågor, live-lager, träffprocent" },
        {
          property: "og:description",
          content:
            "Sveriges enda husbils-matchning som poängsätter riktiga fordon i lager — inte kataloger. Testa gratis.",
        },
        { property: "og:url", content: URL_SELF },
        { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
        { property: "og:image:alt", content: "Husbil-matchning – hitta rätt husbil på 60 sekunder" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      ],
      links: [{ rel: "canonical", href: URL_SELF }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Quiz",
              name: "Husbil-matchning",
              about: "Rekommendation av husbil baserat på personliga preferenser",
              educationalLevel: "beginner",
              provider: {
                "@type": "AutoDealer",
                name: "AK Husbil & Fritid",
                url: "https://www.akhusbilfritid.se",
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: "4.9",
                  reviewCount: "127",
                  bestRating: "5",
                },
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: "Så hittar du rätt husbil",
              description: "Sju korta frågor som matchar dig mot ett riktigt lager av husbilar och husvagnar i realtid.",
              totalTime: "PT2M",
              step: QUESTIONS.map((q, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: q.title,
                text: q.sub,
              })),
            },
            itemListSchema,
            datasetSchema,
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
                { "@type": "ListItem", position: 2, name: "Husbil-matchning", item: URL_SELF },
              ],
            },
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: [".speakable-intro", ".speakable-faq"],
              },
            },
          ]),
        },
      ],
    };
  },
  component: Page,
});

// ---------- Page ----------

function Page() {
  const { data } = useSuspenseQuery(listingsQuery);
  const listings = useMemo(
    () => (data?.items ?? []).filter((l) => l.category === "husbil" || l.category === "husvagn"),
    [data],
  );

  const [quiz, setQuiz] = useState<{ answers: Answers; step: number }>({ answers: {}, step: 0 });
  const { answers, step } = quiz;

  const keepCurrentScroll = () => {
    if (typeof window === "undefined") return () => undefined;
    const y = window.scrollY;
    const restore = () => window.scrollTo({ top: y, left: 0, behavior: "instant" as ScrollBehavior });
    requestAnimationFrame(restore);
    setTimeout(restore, 80);
    setTimeout(restore, 260);
    return restore;
  };

  // Hydrate from URL (?a=...)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const dec = decode(params.get("a"));
    if (Object.keys(dec).length) {
      setQuiz({ answers: dec, step: QUESTIONS.length }); // jump to results
    }
  }, []);

  const complete = step >= QUESTIONS.length;
  const progress = complete ? 100 : Math.round((step / QUESTIONS.length) * 100);

  // Sync answers → URL only when results are shown. Updating the URL on every
  // mobile tap is treated like a navigation by some browsers and jumps to top.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!complete) return;
    const enc = encode(answers);
    const url = new URL(window.location.href);
    if (Object.values(answers).some(Boolean)) url.searchParams.set("a", enc);
    else url.searchParams.delete("a");
    window.history.replaceState({}, "", url.toString());
  }, [answers, complete]);

  const scored = useMemo(() => {
    if (!complete) return [];
    return listings
      .filter((l) => passesHardFilters(answers, l))
      .map((l) => ({ l, score: scoreListing(answers, l) }))
      .filter((m) => m.score >= 70) // extra säkerhet: aldrig visa svaga träffar
      .sort((a, b) => b.score - a.score);
  }, [listings, answers, complete]);

  const topMatches = scored.slice(0, 6);

  return (
    <>
      <PageHeader
        label="Sveriges enda live-matchning"
        title={
          <>
            Hitta din <span className="font-italic-display text-accent">perfekta husbil eller husvagn</span> på 60 sekunder.
          </>
        }
        sub="Sju frågor. Vi jämför mot varje husbil och husvagn i vårt lager just nu och visar exakt vilka som passar dig — med träffprocent. Ingen mail, inget konto."
      />

      <section className="mx-auto w-full max-w-4xl px-4 pb-24 sm:px-6">
        {/* Live stats — E-E-A-T signal & unique dynamic content */}
        <LiveStats listings={listings} />

        {/* Progress bar */}
        <div className="mb-6 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-border/60">
            <div
              className="h-full rounded-full bg-accent transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs font-medium text-muted-foreground">
            {complete ? "Klart" : `Steg ${step + 1} / ${QUESTIONS.length}`}
          </span>
        </div>

        {!complete && (
          <div
            className="min-h-[620px] sm:min-h-[560px]"
            style={{ overflowAnchor: "none" }}
          >
            <QuestionCard
              question={QUESTIONS[step]}
              selected={answers[QUESTIONS[step].key]}
              onPick={(v) => {
                const restoreScroll = keepCurrentScroll();
                setQuiz((current) => {
                  const q = QUESTIONS[current.step];
                  if (!q) return current;
                  return {
                    answers: { ...current.answers, [q.key]: v },
                    step: Math.min(QUESTIONS.length, current.step + 1),
                  };
                });
                restoreScroll();
              }}
              onBack={step > 0 ? () => {
                const restoreScroll = keepCurrentScroll();
                setQuiz((current) => ({ ...current, step: Math.max(0, current.step - 1) }));
                restoreScroll();
              } : undefined}
              index={step}
            />
          </div>
        )}

        {complete && (
          <Results
            matches={topMatches}
            total={listings.length}
            answers={answers}
            onReset={() => {
              setQuiz({ answers: {}, step: 0 });
            }}
          />
        )}

        {/* Trust strip */}
        <div className="mt-12 grid gap-3 rounded-2xl border border-border bg-card/50 p-5 text-sm text-muted-foreground sm:grid-cols-3">
          <span className="flex items-center gap-2"><Check size={14} className="text-accent" /> Live-lager från Fyndanu.se</span>
          <span className="flex items-center gap-2"><Check size={14} className="text-accent" /> Fukttestade & gasoltestade</span>
          <span className="flex items-center gap-2"><Check size={14} className="text-accent" /> Familjeägd sedan 2004</span>
        </div>

        {/* Internal linking — topical authority */}
        <RelatedLinks />


        {/* FAQ (speakable) */}
        <div className="speakable-faq mt-16">
          <h2 className="mb-6 text-2xl font-bold tracking-tight">Vanliga frågor</h2>
          <div className="divide-y divide-border rounded-2xl border border-border bg-card/50">
            {FAQ.map((f) => (
              <details key={f.q} className="group px-5 py-4">
                <summary className="cursor-pointer list-none font-semibold text-foreground marker:hidden">
                  <span className="mr-2 text-accent">›</span>
                  {f.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ---------- Question card ----------

function QuestionCard({
  question,
  selected,
  onPick,
  onBack,
  index,
}: {
  question: Question;
  selected: OptionValue | undefined;
  onPick: (v: OptionValue) => void;
  onBack?: () => void;
  index: number;
}) {
  const Icon = question.icon;
  return (
    <div
      className="animate-in fade-in slide-in-from-bottom-2 rounded-3xl border border-border bg-card p-6 duration-500 sm:p-10"
    >
      <div className="mb-6 flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/10 text-accent">
          <Icon size={20} />
        </span>
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Fråga {index + 1}
        </span>
      </div>
      <h2 className="text-2xl font-bold leading-tight tracking-tight sm:text-3xl">{question.title}</h2>
      <p className="mt-2 text-muted-foreground">{question.sub}</p>

      <div className="mt-6 grid gap-3">
        {question.options.map((o) => {
          const active = selected === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={(event) => {
                event.currentTarget.blur();
                onPick(o.value);
              }}
              className={`group flex items-center justify-between rounded-2xl border p-4 text-left transition ${
                active
                  ? "border-accent bg-accent/5 shadow-[0_0_0_1px_hsl(var(--accent))]"
                  : "border-border bg-background hover:border-accent/60 hover:bg-accent/5"
              }`}
            >
              <div>
                <div className="font-semibold text-foreground">{o.label}</div>
                {o.hint && <div className="text-xs text-muted-foreground">{o.hint}</div>}
              </div>
              <ArrowRight
                size={18}
                className={`transition ${active ? "text-accent" : "text-muted-foreground group-hover:translate-x-1 group-hover:text-accent"}`}
              />
            </button>
          );
        })}
      </div>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft size={14} /> Föregående
        </button>
      )}
    </div>
  );
}

// ---------- Results ----------

function Results({
  matches,
  total,
  answers,
  onReset,
}: {
  matches: { l: FyndanuListing; score: number }[];
  total: number;
  answers: Answers;
  onReset: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const top = matches[0];

  const share = async () => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    if ("share" in navigator) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
          title: "Min husbil-matchning – AK Husbil & Fritid",
          url,
        });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  const summary = useMemo(() => {
    const parts: string[] = [];
    const find = (k: QuestionKey) =>
      QUESTIONS.find((q) => q.key === k)?.options.find((o) => o.value === answers[k])?.label;
    const family = find("family");
    const budget = find("budget");
    const season = find("season");
    const license = find("license");
    const length = find("length");
    if (family) parts.push(family.toLowerCase());
    if (budget) parts.push(budget.toLowerCase());
    if (season) parts.push(season.toLowerCase());
    if (license) parts.push(`körkort ${license}`);
    if (length) parts.push(length.toLowerCase());
    return parts.join(" · ");
  }, [answers]);

  // Rolig persona baserad på svaren — engagerar och gör resultatet delbart.
  const persona = useMemo(() => derivePersona(answers), [answers]);

  return (
    <div className="animate-in fade-in duration-700">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
            <Compass size={12} /> Din matchning
          </div>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
            {top ? (
              <>{matches.length} säkra träffar i lager just nu</>
            ) : (
              "Inga säkra matchningar just nu"
            )}
          </h2>
          {summary && <p className="mt-1 text-sm text-muted-foreground">Baserat på: {summary}</p>}
          {persona && (
            <div className="mt-3 inline-flex max-w-full items-start gap-2 rounded-2xl border border-accent/40 bg-accent/5 px-3 py-2 text-xs sm:text-sm">
              <Sparkles size={14} className="mt-0.5 shrink-0 text-accent" />
              <span>
                <span className="font-semibold text-accent">Din resenärstyp: {persona.title}</span>
                <span className="ml-1 text-muted-foreground">— {persona.blurb}</span>
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={share}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-foreground/80 transition hover:border-accent hover:text-accent"
          >
            <Share2 size={14} /> {copied ? "Länk kopierad" : "Dela resultatet"}
          </button>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-foreground/80 transition hover:border-accent hover:text-accent"
          >
            <RefreshCcw size={14} /> Gör om
          </button>
        </div>
      </div>

      {matches.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-muted-foreground">
            Vi vägrar rekommendera en bil vi inte är 100 % säkra på att den passar dig.
            Vi har {total} fordon totalt, men inget som klarar alla dina hårda krav
            (körkort, sovplatser, budget, längd) just nu.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Ring{" "}
            <a className="font-semibold text-accent" href="tel:0708629743">
              070-862 97 43
            </a>{" "}
            så letar vi personligt åt dig — eller justera ett svar för att bredda urvalet.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {matches.map(({ l, score }, i) => (
          <MatchCard key={l.id} listing={l} score={score} rank={i + 1} answers={answers} />
        ))}
      </div>

      {matches.length >= 2 && <ComparisonTable matches={matches.slice(0, 3)} />}

      <PriceWatchForm
        category={
          answers.vehicleType === "husbil"
            ? "husbil"
            : answers.vehicleType === "husvagn"
              ? "husvagn"
              : "bada"
        }
        maxPrice={budgetCeiling(answers.budget)}
        minSleeps={sleepsMin(answers.family)}
        license={answers.license ?? null}
        answers={answers as unknown as Record<string, unknown>}
      />


      {/* CTA */}
      <div className="mt-10 flex flex-col items-start gap-4 rounded-3xl border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div>
          <h3 className="text-lg font-bold">Osäker på vilken du ska välja?</h3>
          <p className="text-sm text-muted-foreground">
            Vi hjälper dig sortera på 5 minuter. Ingen säljpitch — bara ärliga svar från verkstaden i Kalix.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href="tel:0708629743"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Ring 070-862 97 43
          </a>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-semibold hover:border-accent hover:text-accent"
          >
            Skicka meddelande <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function MatchCard({
  listing: l,
  score,
  rank,
  answers,
}: {
  listing: FyndanuListing;
  score: number;
  rank: number;
  answers: Answers;
}) {
  const pct = Math.round(score);
  const band =
    pct >= 85 ? "Extremt bra match" : pct >= 70 ? "Bra match" : pct >= 55 ? "Okej match" : "Svag match";
  const bandClass =
    pct >= 85
      ? "bg-accent text-accent-foreground"
      : pct >= 70
        ? "bg-accent/20 text-accent"
        : "bg-muted text-muted-foreground";
  const reasons = explainMatch(answers, l);
  const floor = budgetFloor(answers.budget);
  const underBudget =
    floor != null && l.price != null && floor > 0 && l.price < floor
      ? Math.round(((floor - l.price) / floor) * 100)
      : 0;

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border bg-card transition hover:border-accent hover:shadow-xl">
      <Link
        to="/fordon/$id"
        params={{ id: vehicleSlug(l) }}
        className="block"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={l.image}
            alt={l.title}
            loading={rank <= 2 ? "eager" : "lazy"}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
          />
          <div className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-bold ${bandClass}`}>
            {pct}% match
          </div>
          {rank === 1 && (
            <div className="absolute right-3 top-3 rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground">
              #1 bäst
            </div>
          )}
        </div>
        <div className="p-4 pb-2">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{band}</div>
          <h3 className="mt-1 line-clamp-1 font-bold text-foreground group-hover:text-accent">{l.title}</h3>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {l.year && <span>{l.year}</span>}
            {l.length && <span>{l.length} m</span>}
            {l.sleepingSpots && <span>{l.sleepingSpots} sovpl.</span>}
            {l.totalWeight && <span>{l.totalWeight.toLocaleString("sv-SE")} kg</span>}
          </div>
          {reasons.length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-border/60 pt-3 text-xs text-muted-foreground">
              {reasons.map((r) => (
                <li key={r} className="flex items-start gap-1.5">
                  <Check size={12} className="mt-0.5 shrink-0 text-accent" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="font-bold text-foreground">
              {l.price ? `${l.price.toLocaleString("sv-SE")} kr` : "Kontakta oss"}
            </span>
            {underBudget >= 5 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                <TrendingDown size={11} /> {underBudget}% under din budget
              </span>
            )}
          </div>
        </div>
      </Link>
      <div className="grid grid-cols-2 gap-2 px-4 pb-4">
        <a
          href="tel:0708629743"
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
        >
          <Phone size={12} /> Ring säljare
        </a>
        <Link
          to="/kontakt"
          search={{ fordon: l.title, id: l.id }}
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-accent/40 bg-accent/5 px-3 py-2 text-xs font-semibold text-accent transition hover:bg-accent hover:text-accent-foreground"
        >
          <Mail size={12} /> Kontakta om denna
        </Link>
      </div>
    </div>
  );
}

// ---------- Live stats bar ----------
function LiveStats({ listings }: { listings: FyndanuListing[] }) {
  const stats = useMemo(() => {
    const prices = listings.map((l) => l.price).filter((p): p is number => !!p);
    const brands = new Set(listings.map((l) => l.brand).filter(Boolean));
    const winter = listings.filter((l) =>
      /alde|golv[- ]?värme|dieselvärme|isoler|vinter/i.test(l.description),
    ).length;
    return {
      count: listings.length,
      brands: brands.size,
      avg: prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : 0,
      winter,
    };
  }, [listings]);

  return (
    <div className="mb-6 grid grid-cols-2 gap-2 rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent p-4 text-center sm:grid-cols-4">
      <Stat value={stats.count.toString()} label="Fordon i lager" />
      <Stat value={stats.brands.toString()} label="Märken" />
      <Stat value={stats.avg ? `${(stats.avg / 1000).toFixed(0)}k` : "—"} label="Snittpris (kr)" />
      <Stat value={stats.winter.toString()} label="Vinterrustade" />
    </div>
  );
}
function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-display text-2xl font-bold text-accent">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

// ---------- Comparison table (top-3) ----------
function ComparisonTable({ matches }: { matches: { l: FyndanuListing; score: number }[] }) {
  const rows: { label: string; get: (l: FyndanuListing) => string }[] = [
    { label: "Pris", get: (l) => (l.price ? `${l.price.toLocaleString("sv-SE")} kr` : "–") },
    { label: "Årsmodell", get: (l) => l.year?.toString() ?? "–" },
    { label: "Miltal", get: (l) => (l.mileage ? `${l.mileage.toLocaleString("sv-SE")} mil` : "–") },
    { label: "Längd", get: (l) => (l.length ? `${l.length} m` : "–") },
    { label: "Sovplatser", get: (l) => l.sleepingSpots?.toString() ?? "–" },
    { label: "Totalvikt", get: (l) => (l.totalWeight ? `${l.totalWeight.toLocaleString("sv-SE")} kg` : "–") },
    { label: "Bränsle", get: (l) => l.fuel ?? "–" },
    { label: "Växellåda", get: (l) => l.transmission ?? "–" },
  ];
  return (
    <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card">
      <div className="border-b border-border bg-muted/40 px-5 py-3">
        <h3 className="font-bold">Jämför dina topp-3 matchningar</h3>
        <p className="text-xs text-muted-foreground">Sida vid sida — data hämtas live från annonserna.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Spec
              </th>
              {matches.map(({ l, score }) => (
                <th key={l.id} className="p-3 text-left">
                  <div className="line-clamp-1 text-sm font-bold">{l.title}</div>
                  <div className="text-xs font-semibold text-accent">{Math.round(score)}% match</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-border/50 last:border-0">
                <td className="p-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {r.label}
                </td>
                {matches.map(({ l }) => (
                  <td key={l.id} className="p-3 font-medium text-foreground">
                    {r.get(l)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Related internal links (topical authority) ----------
function RelatedLinks() {
  const groups: { title: string; links: { to: string; label: string }[] }[] = [
    {
      title: "Utforska efter märke",
      links: [
        { to: "/husbilar/rapido", label: "Rapido husbilar" },
        { to: "/husbilar/hymer", label: "Hymer husbilar" },
        { to: "/husbilar/adria", label: "Adria husbilar" },
        { to: "/husvagnar/kabe", label: "Kabe husvagnar" },
      ],
    },
    {
      title: "Utforska efter ort",
      links: [
        { to: "/husbil-kalix", label: "Husbil Kalix" },
        { to: "/husbil-lulea", label: "Husbil Luleå" },
        { to: "/husbil-pitea", label: "Husbil Piteå" },
        { to: "/husbil-norrbotten", label: "Husbil Norrbotten" },
      ],
    },
    {
      title: "Verktyg & guider",
      links: [
        { to: "/verktyg/manadskostnad-husbil", label: "Månadskostnad-kalkylator" },
        { to: "/verktyg/korkort-husbil", label: "Vilket körkort krävs?" },
        { to: "/jamfor", label: "Jämför husbil vs husvagn" },
        { to: "/ordlista", label: "Husbils-ordlista" },
      ],
    },
  ];
  return (
    <div className="mt-12 rounded-2xl border border-border bg-card/50 p-6 sm:p-8">
      <h2 className="mb-4 text-lg font-bold">Fördjupa dig</h2>
      <div className="grid gap-6 sm:grid-cols-3">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent">
              {g.title}
            </div>
            <ul className="space-y-1.5">
              {g.links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-muted-foreground transition hover:text-accent"
                  >
                    {l.label} →
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
